import { Bedriftsdata, Produkt } from "codegen";
import { useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { faChevronLeft, faCircleCheck, faShare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { useStrombestillingApiMutation, useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { useAddedMeteringPointLookups, useCustomerMeteringPoints } from "pages/Sikring/opprett/CustomerMeteringPoints";
import { Step1Scope, getMissingFieldsForStep1 } from "pages/Sikring/opprett/Step1Scope";
import { Step2Contract, getInteractiveFields, getMissingFieldsForStep2 } from "pages/Sikring/opprett/Step2Contract";
import { Step3PeriodVolumePrice, getMissingFieldsForStep3 } from "pages/Sikring/opprett/Step3PeriodVolumePrice";
import { Step4Review } from "pages/Sikring/opprett/Step4Review";
import { buildCustomerMeteringPointList } from "pages/Sikring/opprett/customerMeteringPointList";
import { disabledInputStyle } from "pages/Sikring/opprett/disabledInputStyle";
import {
	STEP_NAMES,
	WizardState,
	WizardStep,
	createInitialWizardState,
	getActiveSteps,
	getAddedMaalepunktIds,
	getExcludedMaalepunktIds,
	getFlyt,
	getStepByNumber,
	getStepNumber,
	requiresContact,
	skipsSigning,
} from "pages/Sikring/opprett/wizardTypes";
import {
	calculateVolumeAndPrice,
	describeValidationErrors,
	findOverlappingOrders,
	firstDayOfMonth,
	lastDayOfMonth,
	parseNorwegianNumber,
} from "pages/Sikring/sikringUtils";
import { BekreftOrdreResponse, CreateOrderWithSikringRequest, SikringFlyt, SikringOmfangType, Sikringsordre, ValidationErrors } from "pages/Sikring/types";
import { MaalerMedValidering } from "pages/Strombestilling/orders/components/MeterSearch";

/**
 * Salgskanalen ordren registreres på. Backend validerer verdien mot salgskanal-tabellen
 * (@GyldigSalgskanal), så den kan ikke velges fritt her.
 *
 * MIDLERTIDIG: dette er verdien fra backendens testdata, ikke en bekreftet kanal for adminportalen.
 * Er den ikke i salgskanal-tabellen, avvises hver eneste bestilling med 400.
 */
const SALGSKANAL = "WEB\\hoved";

/** Aktivt steg (1-4). Steg 1 står uten parameter. */
const STEP_PARAM = "steg";

/** Hvor den nye sikringsordren står i oversikten, avhengig av flyten. */
const CREATED_ORDER_STATUS_TEXT: Record<SikringFlyt, string> = {
	[SikringFlyt.SigneringKontraktSikring]: "Sikringsordren ligger som «Venter på signering» i oversikten til kunden har signert.",
	[SikringFlyt.KontraktSikring]: "Sikringsordren ligger som «Venter på kontrakt» i oversikten til UtilityCloud har opprettet kontrakten.",
	[SikringFlyt.KunSikring]: "Sikringen sendes til UtilityCloud med en gang. Følg med på statusen i oversikten.",
};

/** Veiviseren for å opprette en ordre med sikring. Sikring selges bare til bedrift. */
export default function CreateSikringPage() {
	const navigate = useNavigate();

	// Kundeopplysninger. Ligger her, ikke i steg 1, slik at de overlever navigering mellom stegene.
	const [company, setCompany] = useState<Bedriftsdata | undefined>(undefined);
	const [selectedMaalepunkt, setSelectedMaalepunkt] = useState<MaalerMedValidering | null>(null);

	const [wiz, setWiz] = useState<WizardState>(createInitialWizardState());
	const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();
	const stepParam = searchParams.get(STEP_PARAM);
	const currentStep = getStepByNumber(Number(stepParam ?? 1)) ?? WizardStep.Kunde;
	const [createdOrder, setCreatedOrder] = useState<BekreftOrdreResponse | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [formChanged, setFormChanged] = useState<boolean>(false);
	const createMutation = useStrombestillingApiMutation("/ordre/sikring", ["sikringsordre"], "POST", true);
	// Samme spørring og nøkkel som sikringsoversikten, så listen gjenbrukes fra cachen når den er hentet der.
	// Hooken har refetchOnMount: false, så uten en eksplisitt refetch her ville en liste hentet av
	// oversikten tidligere i sesjonen blitt brukt som den er - og overlapp med en sikring opprettet
	// i mellomtiden (av en annen saksbehandler, eller i en annen fane) ville ikke blitt oppdaget.
	const {
		data: existingOrders,
		isPending: overlapCheckLoading,
		isFetching: overlapCheckFetching,
		isError: existingOrdersFailed,
		refetch: refetchExistingOrders,
	} = useStrombestillingApiQuery<Sikringsordre[]>("/sikringsordre", ["sikringsordre"]);
	// isPending alene dekker ikke en bakgrunns-refetch av allerede cachet data, så begge må sjekkes
	// for å holde steget sperret til den ferske listen faktisk har kommet.
	const overlapCheckPending = overlapCheckLoading || overlapCheckFetching;

	useEffect(() => {
		refetchExistingOrders();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		function handlePageUnload(e: BeforeUnloadEvent) {
			e.preventDefault();
		}
		if (formChanged && !createdOrder) {
			globalThis.addEventListener("beforeunload", handlePageUnload);
		}
		return () => {
			globalThis.removeEventListener("beforeunload", handlePageUnload);
		};
	}, [formChanged, createdOrder]);

	// Alle endringer i veiviseren går hit, også bedriftsvalget (som nullstiller maalepunktId).
	function update<K extends keyof WizardState>(field: K, value: WizardState[K]) {
		setWiz((prev) => ({ ...prev, [field]: value }));
		setFormChanged(true);
	}

	const volumeAndPrice = calculateVolumeAndPrice(wiz);
	const overlaps = findOverlappingOrders(existingOrders ?? [], {
		orgnr: company?.orgnr ?? "",
		scope: wiz.scope,
		maalepunktId: wiz.maalepunktId,
		prisomraade: wiz.prisomraade,
		startMonth: wiz.startMonth,
		endMonth: wiz.endMonth,
	});
	const { data: existingCustomerMeteringPoints } = useCustomerMeteringPoints(company?.orgnr ?? "", wiz.prisomraade, wiz.scope === SikringOmfangType.Kunde);
	const addedMeteringPointLookups = useAddedMeteringPointLookups(getAddedMaalepunktIds(wiz));
	// Undefined mens kundens målepunkt hentes, så steg 1 holdes sperret til listen er klar.
	const customerMeteringPointList =
		existingCustomerMeteringPoints &&
		buildCustomerMeteringPointList(
			existingCustomerMeteringPoints,
			getAddedMaalepunktIds(wiz),
			getExcludedMaalepunktIds(wiz),
			addedMeteringPointLookups,
			wiz.prisomraade,
			!wiz.skipContract
		);
	const customerMeteringPoints = customerMeteringPointList?.filter((mp) => mp.selected);

	/**
	 * Målepunktnivå sender det ene målepunktet. Kundenivå med kontrakt sender målepunktene som er valgt i listen i steg 1.
	 * Kundenivå uten kontrakt sender ingen liste, da det ikke er relevant
	 */
	function getMaalepunkterToSend(): string[] | undefined {
		if (wiz.scope === SikringOmfangType.Maalepunkt) return [wiz.maalepunktId];
		if (wiz.skipContract) return undefined;
		return (customerMeteringPoints ?? []).map((mp) => mp.maalepunktId);
	}
	// Interaktive felter står bare på produktet, ikke på spesifikasjonene i produktlisten, så valgt produkt hentes for seg.
	const {
		data: followupProduct,
		isFetching: followupProductLoading,
		isError: followupProductFailed,
	} = useStrombestillingApiQuery<Produkt>(
		`/produkter/${encodeURIComponent(wiz.followupTariffId)}`,
		["products", "followup", wiz.followupTariffId],
		!!wiz.followupTariffId
	);

	// Alle stegene vises alltid, med fast nummer. Steg flyten hopper over vises som overhoppet og navigeres forbi.
	const allSteps = Object.values(WizardStep);
	const activeSteps = getActiveSteps(wiz);
	const stepIndex = activeSteps.indexOf(currentStep);
	const stepNumber = getStepNumber(currentStep);
	const missingFieldsByStep: Record<WizardStep, string[]> = {
		[WizardStep.Kunde]: getMissingFieldsForStep1(wiz, company, customerMeteringPointList, selectedMaalepunkt),
		[WizardStep.Kontrakt]: getMissingFieldsForStep2(wiz, followupProduct),
		[WizardStep.PeriodeVolumPris]: getMissingFieldsForStep3(wiz, volumeAndPrice, overlaps, overlapCheckPending, existingOrdersFailed),
		[WizardStep.Kontroller]: wiz.confirmed ? [] : ["Bekreft at opplysningene stemmer."],
	};
	const stepValid = (step: WizardStep) => missingFieldsByStep[step].length === 0;
	const allStepsValid = activeSteps.every(stepValid);
	const firstInvalidStep = activeSteps.find((step) => !stepValid(step));
	// På kontrollsteget kan et tidligere steg ha blitt ugyldig etter at brukeren gikk videre.
	const blockingStep = currentStep === WizardStep.Kontroller ? firstInvalidStep : stepValid(currentStep) ? undefined : currentStep;

	function goToStep(target: WizardStep, options: { replace?: boolean; state?: WizardHistoryState } = {}) {
		setSearchParams(target === WizardStep.Kunde ? {} : { [STEP_PARAM]: String(getStepNumber(target)) }, options);
	}

	function goForward() {
		// goBack bruker markeringen til å vite at forrige oppføring i historikken er steget før.
		goToStep(activeSteps[stepIndex + 1], { state: { cameFromPreviousStep: true } });
	}

	function goBack() {
		// Uten markering (lenke, oppdatert side) finnes ikke steget før i historikken.
		if ((location.state as WizardHistoryState | null)?.cameFromPreviousStep) navigate(-1);
		else goToStep(activeSteps[stepIndex - 1], { replace: true });
	}

	// Nytt steg starter øverst, også ved tilbake og nettleserens frem/tilbake-knapper. Kjøres etter at det nye
	// steget er rendret (currentStep kommer fra URL-en), og før det tegnes, så siden ikke hopper.
	useLayoutEffect(() => {
		window.scrollTo(0, 0);
	}, [currentStep]);

	// Veiviseren ligger bare i minnet, så et steg i URL-en kan være utenfor rekkevidde (oppdatert side, lenke,
	// fremover-knapp). Kjøres bare når URL-en endres, så en bakgrunnshenting ikke kaster brukeren bakover -
	// derfor er avhengighetene utelatt.
	useEffect(() => {
		const lastReachable = firstInvalidStep ?? WizardStep.Kontroller;
		const index = activeSteps.indexOf(currentStep);
		if (index === -1 || index > activeSteps.indexOf(lastReachable)) goToStep(lastReachable, { replace: true });
		else if (stepParam !== null && !getStepByNumber(Number(stepParam))) goToStep(currentStep, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepParam]);

	async function submit() {
		if (createMutation.isPending || !company?.orgnr || !allStepsValid) return;
		setErrorMessage(null);

		// Valideringen skal allerede ha stoppet ugyldige tall, men sjekkes her også så "null" aldri sendes som prisvariabel.
		const interactiveFieldEntries = getInteractiveFields(followupProduct).map(
			(field) => [field, parseNorwegianNumber(wiz.interactiveFieldValues[field])] as const
		);
		if (!wiz.skipContract && interactiveFieldEntries.some(([, value]) => value === null)) {
			setErrorMessage("Ett eller flere prisfelter for oppfølgingsproduktet mangler eller er ugyldige.");
			return;
		}

		const fakturaAdresse = requiresContact(wiz) ? wiz.invoiceAddress : company.adresse;
		const request: CreateOrderWithSikringRequest = {
			kilde: SALGSKANAL,
			kunde: {
				organisasjonsnummer: company.orgnr,
				// Fødselsnummer er bare påkrevd når kontaktpersonen skal signere, og sendes ikke ellers.
				ssn: skipsSigning(wiz) ? undefined : wiz.contactFodselsnummer,
				// Uten kontrakt er kontaktpersonen valgfri og feltene låst, så tom verdi sendes.
				fornavn: requiresContact(wiz) ? wiz.contactFirstName.trim() : "",
				etternavn: requiresContact(wiz) ? wiz.contactLastName.trim() : "",
				mobil: requiresContact(wiz) ? wiz.contactMobile.trim() : "",
				epost: requiresContact(wiz) ? wiz.contactEmail.trim() : "",
				// Fakturaadressen kan bare endres når ordren har kontrakt. Ellers sendes bedriftsadressen som før.
				adresse: {
					gate: fakturaAdresse?.gate ?? "",
					husnummer: fakturaAdresse?.husnummer || undefined,
					postnummer: fakturaAdresse?.postnummer ?? "",
					poststed: fakturaAdresse?.poststed ?? "",
				},
			},
			maalepunkter: getMaalepunkterToSend(),
			sikringRequest: {
				omfangType: wiz.scope,
				prisomraade: wiz.scope === SikringOmfangType.Kunde ? wiz.prisomraade : undefined,
				start: firstDayOfMonth(wiz.startMonth),
				slutt: lastDayOfMonth(wiz.endMonth),
				volumPerMaanedKwh: volumeAndPrice.monthlyVolumes ?? {},
				innpris: volumeAndPrice.innpris ?? 0,
				fortjeneste: volumeAndPrice.fortjeneste ?? 0,
				flyt: getFlyt(wiz),
				skipEnergiMail: wiz.skipEnergiMail,
			},
			paaslag: wiz.skipContract ? undefined : (parseNorwegianNumber(wiz.paaslag) ?? undefined),
			fastbelop: wiz.skipContract ? undefined : (parseNorwegianNumber(wiz.fastbelop) ?? undefined),
			oppfolgingsprodukt: wiz.skipContract
				? undefined
				: {
						tariffId: wiz.followupTariffId,
						interaktiveFelter: Object.fromEntries(interactiveFieldEntries.map(([field, value]) => [field, String(value)])),
					},
		};

		try {
			const response = await createMutation.mutateAsync(request);
			if (response.status === 400) {
				const validation = (await response.json()) as ValidationErrors;
				setErrorMessage(`Ordren ble avvist: ${describeValidationErrors(validation.errors ?? [])}`);
				return;
			}
			if (!response.ok) {
				setErrorMessage("Noe gikk galt ved opprettelse av ordren med sikring.");
				return;
			}
			setCreatedOrder((await response.json()) as BekreftOrdreResponse);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			setErrorMessage(`Noe gikk galt ved opprettelse av ordren med sikring: ${message}`);
		}
	}

	if (createdOrder) {
		return (
			<Success>
				<IconCircle>
					<FontAwesomeIcon icon={faCircleCheck} />
				</IconCircle>
				<h2 style={{ margin: "0 0 10px" }}>Ordren med sikring er opprettet</h2>
				<p style={{ margin: "0 0 8px", color: colors.grey800, lineHeight: 1.6 }}>{CREATED_ORDER_STATUS_TEXT[getFlyt(wiz)]}</p>
				{!skipsSigning(wiz) && createdOrder.samtykkeRedirect && (
					<p style={{ margin: "0 0 18px", fontSize: "0.875rem" }}>
						Samtykkelenke:{" "}
						<a href={createdOrder.samtykkeRedirect} target="_blank" rel="noreferrer">
							{createdOrder.samtykkeRedirect}
						</a>
					</p>
				)}
				<Button type="button" label="Til sikringsoversikten" onClick={() => navigate("/sikring")} />
			</Success>
		);
	}

	return (
		<Flex direction={Direction.column} gap="1rem">
			<Link to="/sikring">
				<FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: "6px", fontSize: "0.75rem" }} />
				Avbryt og gå tilbake
			</Link>
			<h1 style={{ margin: "14px 0 6px" }}>Opprett sikring</h1>
			{company && (
				<p style={{ margin: "0 0 26px", color: colors.grey800 }}>
					{company.navn} (orgnr. {company.orgnr})
				</p>
			)}

			<Steps>
				{allSteps.map((step, i) => {
					const skipped = !activeSteps.includes(step);
					const active = step === currentStep;
					const done = !skipped && i < stepNumber - 1;
					return (
						<StepItem key={step}>
							{/* Streken farges etter hvor langt brukeren har kommet, så den ikke brytes ved et overhoppet steg. */}
							{i > 0 && <StepLine $color={i < stepNumber ? colors.blue : colors.grey300} />}
							<StepCircle $active={active} $done={done} $skipped={skipped}>
								{skipped ? <FontAwesomeIcon icon={faShare} /> : done ? "✓" : i + 1}
							</StepCircle>
							<StepText $active={active}>
								{STEP_NAMES[step]}
								{skipped && <SkippedText>Hoppes over</SkippedText>}
							</StepText>
						</StepItem>
					);
				})}
			</Steps>

			{/*
			 * Steg 1 avmonteres ikke når man går videre: MeterSearch nullstiller valgt målepunkt i en
			 * effekt som også kjører ved montering, så en avmontering her ville tømt målepunktvalget
			 * hver gang brukeren gikk frem og tilbake mellom stegene.
			 */}
			<StepPanel $visible={currentStep === WizardStep.Kunde}>
				<Step1Scope
					wiz={wiz}
					update={update}
					company={company}
					setCompany={(newCompany) => {
						setCompany(newCompany);
						setSelectedMaalepunkt(null);
						update("maalepunktId", "");
						update("addedMaalepunktIds", []);
						update("excludedMaalepunktIds", []);
						update("invoiceAddress", newCompany?.adresse ?? null);
					}}
					selectedMaalepunkt={selectedMaalepunkt}
					setSelectedMaalepunkt={setSelectedMaalepunkt}
				/>
			</StepPanel>
			{currentStep === WizardStep.Kontrakt && (
				<Step2Contract
					stepNumber={stepNumber}
					wiz={wiz}
					update={update}
					followupProduct={followupProduct}
					followupProductLoading={followupProductLoading}
					followupProductFailed={followupProductFailed}
				/>
			)}
			{currentStep === WizardStep.PeriodeVolumPris && (
				<Step3PeriodVolumePrice
					stepNumber={stepNumber}
					wiz={wiz}
					volumeAndPrice={volumeAndPrice}
					overlaps={overlaps}
					overlapCheckPending={overlapCheckPending}
					overlapCheckFailed={existingOrdersFailed}
					onRetryOverlapCheck={refetchExistingOrders}
					customerMeteringPoints={customerMeteringPoints ?? []}
					update={update}
				/>
			)}
			{currentStep === WizardStep.Kontroller && company && (
				<Step4Review
					stepNumber={stepNumber}
					wiz={wiz}
					followupProduct={followupProduct}
					company={company}
					volumeAndPrice={volumeAndPrice}
					customerMeteringPoints={customerMeteringPoints}
					update={update}
				/>
			)}

			{errorMessage && <ErrorText>{errorMessage}</ErrorText>}

			<Flex direction={Direction.row} gap="14px" alignItems={Justify.center} style={{ marginTop: "10px" }}>
				{stepIndex > 0 && <Button secondary type="button" label="Tilbake" onClick={goBack} />}
				{stepIndex < activeSteps.length - 1 && (
					<Button type="button" label="Neste" disabled={!stepValid(currentStep)} onClick={() => stepValid(currentStep) && goForward()} />
				)}
				{currentStep === WizardStep.Kontroller && (
					<Button
						type="button"
						label={createMutation.isPending ? "Oppretter …" : "Opprett sikring"}
						loading={createMutation.isPending}
						disabled={createMutation.isPending || !allStepsValid}
						onClick={submit}
					/>
				)}
				<Link to="/sikring" style={{ marginLeft: "6px" }}>
					Avbryt
				</Link>
			</Flex>
			{blockingStep && <MissingText>{missingText(blockingStep, missingFieldsByStep[blockingStep])}</MissingText>}
		</Flex>
	);
}

interface WizardHistoryState {
	cameFromPreviousStep?: boolean;
}

function missingText(step: WizardStep, missing: string[]): string {
	return `Steg ${getStepNumber(step)} (${STEP_NAMES[step]}): ${missing.join(" ")}`;
}

const MissingText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

// Skjuler i stedet for å avmontere, slik at stegets barn beholder sin egen tilstand.
const StepPanel = styled.div<{ $visible: boolean }>`
	display: ${(props) => (props.$visible ? "block" : "none")};
`;

const Steps = styled.div`
	display: flex;
	margin-bottom: 32px;
	max-width: 820px;
`;

const StepItem = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	position: relative;
`;

const StepLine = styled.div<{ $color: string }>`
	position: absolute;
	top: 16px;
	right: 50%;
	width: 100%;
	height: 2px;
	background: ${(props) => props.$color};
`;

const StepCircle = styled.div<{ $active: boolean; $done: boolean; $skipped: boolean }>`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 700;
	font-size: 0.875rem;
	z-index: 1;
	position: relative;
	${(props) =>
		props.$active
			? css`
					background: ${colors.blue};
					color: ${colors.white};
				`
			: props.$done
				? css`
						background: ${colors.green700};
						color: ${colors.white};
					`
				: props.$skipped
					? css`
							box-sizing: border-box;
							background: ${colors.white};
							color: ${colors.grey500};
							border: 2px dashed ${colors.grey500};
						`
					: css`
							background: ${colors.white};
							color: ${colors.grey700};
							box-shadow: inset 0 0 0 1px ${colors.grey500};
						`}
`;

const StepText = styled.div<{ $active: boolean }>`
	font-weight: ${(props) => (props.$active ? 700 : 400)};
	font-size: 0.8125rem;
	color: ${(props) => (props.$active ? colors.grey900 : colors.grey700)};
	text-align: center;
	max-width: 150px;
`;

const SkippedText = styled.span`
	display: block;
	font-size: 0.75rem;
	font-style: italic;
`;

const ErrorText = styled.p`
	color: ${colors.coral};
`;

const Success = styled.div`
	max-width: 560px;
	margin: 60px auto 0;
	text-align: center;
`;

const IconCircle = styled.div`
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: ${colors.green200};
	color: ${colors.green700};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.75rem;
	margin: 0 auto 18px;
`;

// Felles for stegene i veiviseren.

export const StepWrapper = styled.div<{ $gap?: string; $maxWidth?: string }>`
	max-width: ${(props) => props.$maxWidth ?? "640px"};
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.$gap ?? "18px"};
`;

export const StepHeading = styled.h2`
	margin: 0 0 18px;
`;

export const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 18px;
`;

export const Label = styled.label`
	display: block;
	font-weight: 700;
	font-size: 1rem;
	color: ${colors.grey800};
	margin-bottom: 8px;
`;

export const Hint = styled.p`
	font-size: 0.8125rem;
	line-height: 1.4;
	color: ${colors.grey800};
	margin: 8px 0 0;
`;

export const Box = styled.div`
	padding: 16px 18px;
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
`;

export const FieldLabel = styled.label`
	display: block;
	font-weight: 700;
	font-size: 0.8125rem;
	color: ${colors.grey800};
	margin-bottom: 6px;
`;

export const FieldError = styled.p`
	font-size: 0.75rem;
	color: ${colors.coral};
	margin: 6px 0 0;
`;

// Høyde settes bevisst ikke her - globalt skjemastil (GlobalStyling) gir padding og fontstørrelse,
// og en fast høyde i tillegg klipper teksten i feltet.
export const Select = styled.select`
	width: 100%;
	box-sizing: border-box;
`;

// Høyde settes bevisst ikke her - globalt skjemastil (GlobalStyling) gir padding og fontstørrelse,
// og en fast høyde i tillegg klipper teksten i feltet.
export const Input = styled.input`
	width: 100%;
	box-sizing: border-box;

	&:disabled {
		${disabledInputStyle}
	}
`;
